import { Alert } from "@/core/components/ui/alert/alert";
import { Button } from "@/core/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/core/components/ui/dialog";
import { Stack } from "@/core/components/ui/stack";
import { Typography } from "@/core/components/ui/typography";
import type { OnboardingDocument } from "./types";

interface DeleteDocumentModalProps {
  document: OnboardingDocument | null;
  isDeleting: boolean;
  error: string | null;
  onCancel: () => void;
  onConfirm: () => void;
}

export const DeleteDocumentModal = ({
  document,
  isDeleting,
  error,
  onCancel,
  onConfirm,
}: DeleteDocumentModalProps) => {
  const name = document ? document.file_name || document.title : "";
  const consequence =
    "It is removed for good. You can upload it again if you still need it.";

  return (
    <Dialog
      open={!!document}
      onOpenChange={(open) => {
        // Dismissing mid-delete would hide the outcome, so hold the modal open.
        if (!open && !isDeleting) onCancel();
      }}
    >
      <DialogContent description={`Delete ${name}? ${consequence}`}>
        <DialogHeader>
          <DialogTitle>Delete document</DialogTitle>
        </DialogHeader>

        <Stack className="p-6" gap="lg" fullWidth>
          <Typography variant="bodyW1" textColor="secondary">
            Delete{" "}
            <span className="font-semibold text-primary-text">{name}</span>?{" "}
            {consequence}
          </Typography>
          {error && <Alert variant="destructive">{error}</Alert>}
        </Stack>

        <DialogFooter>
          <Button
            variant="secondary"
            size="sm"
            type="button"
            disabled={isDeleting}
            onClick={onCancel}
          >
            Cancel
          </Button>
          <Button
            variant="destructive"
            size="sm"
            type="button"
            loading={isDeleting}
            disabled={isDeleting}
            onClick={onConfirm}
          >
            Delete
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
